import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import SEO from '../components/SEO';
import { getSortedPostsData } from '../data/posts';

function DesignExplore() {
  const posts = getSortedPostsData();
  const recentPosts = posts.slice(0, 5);

  const dailyThoughts = [
    { id: '1', date: '2025-01-15', content: 'Late night coding. Everything clicks at 2am.' },
    { id: '2', date: '2025-01-14', content: 'Built something today. Shipped it. Felt good.' },
  ];
  
  const projects = [
    { id: '1', name: 'Atris Labs', description: 'Building AI systems', status: 'active' },
    { id: '2', name: 'Popshare', description: 'Writing tool for flow state', status: 'active' },
  ];

  const getExcerpt = (content, maxLength = 200) => {
    if (!content) return '';
    const plainText = content
      .replace(/#{1,6}\s+/g, '')
      .replace(/\*\*/g, '')
      .replace(/\*/g, '')
      .replace(/---/g, '')
      .trim();
    if (plainText.length <= maxLength) return plainText;
    return plainText.substring(0, maxLength).trim() + '...';
  };

  return (
    <div className="min-h-screen bg-black text-white">
      <SEO />
      <div className="max-w-[60ch] mx-auto px-4 py-16">
        <header className="mb-20">
          <Link to="/" className="text-gray-500 hover:text-white text-sm mb-8 inline-block">
            ← Back
          </Link>
          <h1 className="text-3xl font-bold mb-2">Design</h1>
          <p className="text-gray-500">Three approaches</p>
        </header>

        {/* Variant 1: Typography-First */}
        <section className="mb-32">
          <div className="space-y-12">
            {recentPosts.map((post) => (
              <article key={post.id}>
                <Link to={`/blog/${post.id}`} className="block group">
                  <div className="text-xs text-gray-600 font-mono mb-3 tracking-wide">{post.date}</div>
                  <h2 className="text-2xl font-bold mb-4 text-white group-hover:text-gray-300 transition-colors leading-tight">
                    {post.title}
                  </h2>
                  <p className="text-gray-400 text-base leading-relaxed">
                    {getExcerpt(post.content, 200)}
                  </p>
                </Link>
              </article>
            ))}
          </div>
        </section>

        {/* Variant 2: With Navigation */}
        <SegmentedControl posts={recentPosts} dailyThoughts={dailyThoughts} projects={projects} getExcerpt={getExcerpt} />

        {/* Variant 3: Subtle Motion */}
        <ScrollReveal posts={recentPosts} getExcerpt={getExcerpt} />

      </div>
    </div>
  );
}

function SegmentedControl({ posts, dailyThoughts, projects, getExcerpt }) {
  const [activeTab, setActiveTab] = useState('essays');

  return (
    <section className="mb-32">
      <div className="mb-12">
        <div className="inline-flex bg-gray-900 p-1 rounded border border-gray-800">
          <button
            onClick={() => setActiveTab('essays')}
            className={`px-5 py-2 text-sm rounded transition-colors ${
              activeTab === 'essays' 
                ? 'bg-white text-black' 
                : 'text-gray-400 hover:text-white'
            }`}
          >
            Essays
          </button>
          <button
            onClick={() => setActiveTab('daily')}
            className={`px-5 py-2 text-sm rounded transition-colors ${
              activeTab === 'daily' 
                ? 'bg-white text-black' 
                : 'text-gray-400 hover:text-white'
            }`}
          >
            Daily
          </button>
          <button
            onClick={() => setActiveTab('projects')}
            className={`px-5 py-2 text-sm rounded transition-colors ${
              activeTab === 'projects' 
                ? 'bg-white text-black' 
                : 'text-gray-400 hover:text-white'
            }`}
          >
            Projects
          </button>
        </div>
      </div>

      <div>
        {activeTab === 'essays' && (
          <div className="space-y-12">
            {posts.map((post) => (
              <article key={post.id}>
                <Link to={`/blog/${post.id}`} className="block group">
                  <div className="text-xs text-gray-600 font-mono mb-3 tracking-wide">{post.date}</div>
                  <h2 className="text-2xl font-bold mb-4 text-white group-hover:text-gray-300 leading-tight">
                    {post.title}
                  </h2>
                  <p className="text-gray-400 text-base leading-relaxed">
                    {getExcerpt(post.content, 200)}
                  </p>
                </Link>
              </article>
            ))}
          </div>
        )}
        {activeTab === 'daily' && (
          <div className="space-y-8">
            {dailyThoughts.map((thought) => (
              <div key={thought.id} className="border-l-2 border-gray-800 pl-6 py-1">
                <div className="text-xs text-gray-600 font-mono mb-2 tracking-wide">{thought.date}</div>
                <p className="text-gray-300 text-base leading-relaxed">{thought.content}</p>
              </div>
            ))}
          </div>
        )}
        {activeTab === 'projects' && (
          <div className="space-y-6">
            {projects.map((project) => (
              <div key={project.id} className="border border-gray-800 p-6 rounded">
                <h3 className="text-xl font-bold mb-2 text-white">{project.name}</h3>
                <p className="text-gray-400 text-base leading-relaxed mb-3">{project.description}</p>
                <div className="text-xs text-gray-600 font-mono uppercase tracking-wide">{project.status}</div>
              </div>
            ))}
          </div>
        )}
      </div>
    </section>
  );
}

function ScrollReveal({ posts, getExcerpt }) {
  return (
    <section className="mb-32">
      <div className="space-y-12">
        {posts.map((post, idx) => (
          <motion.article
            key={post.id}
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-50px" }}
            transition={{ duration: 0.4, delay: idx * 0.08 }}
          >
            <Link to={`/blog/${post.id}`} className="block group">
              <div className="text-xs text-gray-600 font-mono mb-3 tracking-wide">{post.date}</div>
              <h2 className="text-2xl font-bold mb-4 text-white group-hover:text-gray-300 transition-colors leading-tight">
                {post.title}
              </h2>
              <p className="text-gray-400 text-base leading-relaxed">
                {getExcerpt(post.content, 200)}
              </p>
            </Link>
          </motion.article>
        ))}
      </div>
    </section>
  );
}

export default DesignExplore;
